import { Controller } from "@hotwired/stimulus"

// 領域単位編集 (doc/issues/260530_bot_managed_region_ownership_design.md §10/§11) のインライン編集面。
//
// 単一の contenteditable な本文エリアに、Bot 管理領域を contenteditable=false の atomic チップとして
// 埋め込む。人間はチップの前後を最初から自由に編集でき (ボタン/追加操作なし)、Bot チップは編集も
// 削除もできない。送信値はチップの data-canonical から取る (DOM 表示の改変を無効化し、サーバの
// sha 照合と二重防御)。編集面の DOM は connect 時に segments から JS が構築する。
//
// serialize: 編集面の子ノードを順に走査し、Bot チップ→bot ブロック、その間のテキスト→human ブロック
// として単一 hidden JSON (blocksJson) に書く。順序 (order) は DOM 順。サーバ reconcile が §23.1 の
// 写像 (bot は content を sha 照合し canonical を採用 / human は text を採用) で受ける。
export default class extends Controller {
  static targets = ["surface", "blocksJson"]
  static values = { segments: Array }

  connect() {
    this.renderSurface()
    this.serialize()
  }

  // 編集面を segments から構築する。各 Bot チップの後ろに編集可能な改行を置き、最初から前後に
  // 入力できる場所を保証する (normalize が余分な改行を吸収するため空のままなら human にならない)。
  renderSurface() {
    const surface = this.surfaceTarget
    surface.textContent = ""
    const segments = this.segmentsValue

    if (segments[0]?.kind === "bot") surface.appendChild(document.createTextNode("\n"))
    segments.forEach((segment) => {
      if (segment.kind === "bot") {
        surface.appendChild(this.buildChip(segment))
        surface.appendChild(document.createTextNode("\n"))
      } else {
        surface.appendChild(document.createTextNode(segment.text || ""))
      }
    })
    surface.appendChild(document.createTextNode("\n"))
  }

  // Bot チップ DOM。textContent で組むため canonical に HTML が含まれても要素化しない (XSS なし)。
  buildChip(segment) {
    const chip = document.createElement("div")
    chip.contentEditable = "false"
    chip.className = "region-bot-chip my-2 select-none rounded border border-indigo-200 bg-indigo-50 px-3 py-2"
    chip.dataset.kind = "bot"
    chip.dataset.id = segment.id
    chip.dataset.canonical = segment.canonical

    const label = document.createElement("div")
    label.className = "mb-1 text-xs font-medium text-indigo-700"
    label.textContent = "🔒 自動更新される部分（編集できません）"

    // 中身は登録情報を流し込む記法で、読んでも編集の役に立たないので 2 行に畳む。全文は title で読める
    const body = document.createElement("div")
    body.className = "line-clamp-2 whitespace-pre-wrap text-sm text-gray-800"
    body.title = segment.canonical
    body.textContent = segment.canonical

    chip.append(label, body)
    return chip
  }

  // Enter は単純な改行 (\n) 挿入にする。ブラウザ既定の <div>/<br> 生成や、Bot チップ前の空行が
  // 消えて入力不能になる不具合 (§24) を避けるため、自前で改行テキストを挿入する。
  onKeydown(event) {
    // 日本語入力の変換を確定する Enter は改行にしない (Safari は確定の keydown を compositionend の後に送るので keyCode も見る)
    if (event.isComposing || event.keyCode === 229) return
    if (event.key !== "Enter" || event.shiftKey) return

    event.preventDefault()
    const selection = window.getSelection()
    if (!selection.rangeCount) return

    const range = selection.getRangeAt(0)
    range.deleteContents()
    const newline = document.createTextNode("\n")
    range.insertNode(newline)
    range.setStartAfter(newline)
    range.collapse(true)
    selection.removeAllRanges()
    selection.addRange(range)
    this.serialize()
  }

  // Bot チップの編集・削除を防ぐ。削除対象範囲がチップに重なるなら入力をキャンセルする
  // (取りこぼしてもサーバが bot_region_tampered / bot_region_missing で 422 にする最終防壁あり)。
  onBeforeInput(event) {
    if (!event.inputType?.startsWith("delete")) return

    const targetRanges = event.getTargetRanges?.() || []
    const chips = [...this.surfaceTarget.querySelectorAll("[data-kind='bot']")]
    const hitsChip = targetRanges.some((staticRange) => {
      const range = document.createRange()
      range.setStart(staticRange.startContainer, staticRange.startOffset)
      range.setEnd(staticRange.endContainer, staticRange.endOffset)
      return chips.some((chip) => range.intersectsNode(chip))
    })
    if (hitsChip) event.preventDefault()
  }

  serialize() {
    const blocks = []
    let human = ""
    const flushHuman = () => {
      // 中身が空白/改行のみ (チップ周りの編集可能な余白) なら human にしない。normalize が前後改行を吸収する。
      if (human.trim() !== "") blocks.push({ kind: "human", text: human })
      human = ""
    }

    this.surfaceTarget.childNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE && node.dataset?.kind === "bot") {
        flushHuman()
        // 送信値は data-canonical から (DOM 改変を無効化)。POST 契約 (§23.1) のキーは content。
        blocks.push({ kind: "bot", id: node.dataset.id, content: node.dataset.canonical })
      } else {
        human += this.nodeText(node)
      }
    })
    flushHuman()

    blocks.forEach((block, order) => { block.order = order })
    this.blocksJsonTarget.value = JSON.stringify(blocks)
  }

  // ノードのテキスト化 (編集中に挿入される br / div を改行に正規化する)。
  nodeText(node) {
    if (node.nodeType === Node.TEXT_NODE) return node.nodeValue
    if (node.nodeName === "BR") return "\n"
    if (node.nodeType !== Node.ELEMENT_NODE) return ""

    let text = ""
    node.childNodes.forEach((child) => { text += this.nodeText(child) })
    return node.nodeName === "DIV" || node.nodeName === "P" ? `\n${text}` : text
  }
}
